import { Dispatch, SetStateAction } from "react"
import { SpinnerText } from "../ui/Spinner"
import {
  FiChevronDown,
  FiChevronUp,
  FiEdit2,
  FiTrash2
} from "react-icons/fi"
import QuestionBox from "../ui/QuestionBox"
import { DesignerSearchResult } from "../../services/designerService"

type DesignerMode = "database" | "new" | "waiver" | null

type Designer = {
  id: string
  name: string
}

type DesignerDetailsSectionProps = {
  designerDetailsOpen: boolean
  setDesignerDetailsOpen: (value: boolean) => void
  designerMode: DesignerMode
  setDesignerMode: (value: DesignerMode) => void
  designerName: string
  setDesignerName: (value: string) => void
  designers: Designer[]
  setDesigners: Dispatch<SetStateAction<Designer[]>>
  designerSearch: string
  setDesignerSearch: (value: string) => void
  designerSearchResults: DesignerSearchResult[]
  setDesignerSearchResults: Dispatch<SetStateAction<DesignerSearchResult[]>>
  selectedDesigner: DesignerSearchResult | null
  setSelectedDesigner: (value: DesignerSearchResult | null) => void
  designerDatabaseOpen: boolean
  setDesignerDatabaseOpen: (value: boolean) => void
  designerSearchLoading: boolean
  designerSearchError: string
  editingDesignerIndex: number | null
  setEditingDesignerIndex: (value: number | null) => void
  editingDesignerName: string
  setEditingDesignerName: (value: string) => void
  resetDesignerSearch: () => void
  handleAddDesigner: () => void
  handleAddDatabaseDesigner: () => void
  openEditDesigner: (index: number) => void
  saveEditedDesigner: () => void
}

export default function DesignerDetailsSection({
  designerDetailsOpen,
  setDesignerDetailsOpen,
  designerMode,
  setDesignerMode,
  designerName,
  setDesignerName,
  designers,
  setDesigners,
  designerSearch,
  setDesignerSearch,
  designerSearchResults,
  setDesignerSearchResults,
  selectedDesigner,
  setSelectedDesigner,
  designerDatabaseOpen,
  setDesignerDatabaseOpen,
  designerSearchLoading,
  designerSearchError,
  editingDesignerIndex,
  setEditingDesignerIndex,
  editingDesignerName,
  setEditingDesignerName,
  resetDesignerSearch,
  handleAddDesigner,
  handleAddDatabaseDesigner,
  openEditDesigner,
  saveEditedDesigner
}: DesignerDetailsSectionProps) {
  return (
<div className="border-b-2 border-dashed border-gray-300 py-4">
  <div className="flex items-center gap-3">
    <button
      type="button"
      onClick={() => setDesignerDetailsOpen(!designerDetailsOpen)}
      className={`relative h-8 w-16 rounded-full transition-colors ${
        designerDetailsOpen ? "bg-blue-800" : "bg-gray-400"
      }`}
      aria-pressed={designerDetailsOpen}
    >
      <span
        className={`absolute left-1 top-1 h-6 w-6 rounded-full bg-white transition-transform ${
          designerDetailsOpen ? "translate-x-8" : "translate-x-0"
        }`}
      />
    </button>

    <h4 className="text-lg font-semibold text-blue-800">
      Designer details
    </h4>

    <QuestionBox title="Designer details">
      In this section you can provide the name of the designer or
      add a new designer, or indicate that the designer has waived
      the right to be named.
    </QuestionBox>
  </div>

  {designerDetailsOpen && (
    <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-3">
      <button
        type="button"
        onClick={() => {
          setDesignerMode("database")
          resetDesignerSearch()
        }}
        className={`rounded border px-4 py-4 text-sm font-semibold uppercase text-blue-800 ${
          designerMode === "database"
            ? "border-blue-800 bg-blue-50"
            : "border-transparent bg-blue-50 hover:bg-blue-100"
        }`}
      >
        Add designer from our database
      </button>

      <button
        type="button"
        onClick={() => {
          setDesignerMode("new")
          resetDesignerSearch()
        }}
        className={`rounded border px-4 py-4 text-sm font-semibold uppercase text-blue-800 ${
          designerMode === "new"
            ? "border-blue-800 bg-blue-50"
            : "border-transparent bg-blue-50 hover:bg-blue-100"
        }`}
      >
        Add new designer
      </button>

      <div className="relative group">
        <button
          type="button"
          onClick={() => {
            setDesignerMode("waiver")
            setDesignerName("")
            resetDesignerSearch()
          }}
          className={`h-full w-full rounded border px-4 py-4 text-sm font-semibold uppercase ${
            designerMode === "waiver"
              ? "border-blue-800 bg-gray-300 text-blue-800"
              : "border-transparent bg-blue-50 text-blue-800 hover:bg-blue-100"
          }`}
        >
          Waiver of the right to be named
        </button>

        <div className="pointer-events-none absolute left-1/2 top-full z-30 mt-2 hidden w-96 -translate-x-1/2 rounded-md border border-blue-500 bg-blue-50 p-3 text-left text-sm text-blue-900 shadow-md group-hover:block">
          <p>
            The designer has the right to their design. However,
            if a design is developed by an employee under the
            control of an employer, the employer has the right to
            the design. In the latter case, the designer still has
            the right to be named.
          </p>
          <p className="mt-2">
            If the designer does not wish to be named, the
            applicant should indicate that the designer has waived
            this right.
          </p>
        </div>
      </div>
    </div>
  )}

  {designerDetailsOpen && designerMode === "waiver" && (
    <p className="mt-4 text-sm text-gray-500">
      The designer has waived the right to be named.
    </p>
  )}

  {designerDetailsOpen && designers.length > 0 && (
    <div className="mt-4 overflow-hidden rounded border border-gray-400">
      <div className="grid grid-cols-[1fr_2fr_80px] border-b border-gray-400 bg-gray-50 text-sm font-semibold text-gray-800">
        <div className="px-4 py-3">Designer ID</div>
        <div className="px-4 py-3">Designer name</div>
        <div className="px-4 py-3"></div>
      </div>

      {designers.map((designer, index) => (
        <div
          key={`${designer.id || designer.name}-${index}`}
          className="grid grid-cols-[1fr_2fr_80px] items-center border-b border-gray-200 text-sm last:border-b-0"
        >
          <div className="px-4 py-3 text-gray-800">
            {designer.id || "-"}
          </div>
          <div className="px-4 py-3 text-gray-800">
            {designer.name}
          </div>
          <div className="flex justify-end gap-3 px-4 py-3 text-blue-800">
            <button
              type="button"
              onClick={() => openEditDesigner(index)}
              className="hover:text-blue-700"
            >
              <FiEdit2 className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() =>
                setDesigners(prev =>
                  prev.filter((_, itemIndex) => itemIndex !== index)
                )
              }
              className="hover:text-blue-700"
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  )}

  {designerDetailsOpen && designerMode === "database" && (
    <div className="relative mt-5 rounded bg-gray-50 p-4">
      <button
        type="button"
        onClick={() => {
          setDesignerMode(null)
          resetDesignerSearch()
        }}
        className="absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-500 text-xs font-semibold text-white hover:bg-gray-600"
      >
        x
      </button>

      <label className="block text-sm text-gray-500">
        Enter name or ID
      </label>

      <div className="mt-2 flex gap-4 pr-8">
        <div className="relative flex-1">
          <div className="relative">
            <input
              type="text"
              value={designerSearch}
              onChange={(event) => {
                setDesignerSearch(event.target.value)
                setSelectedDesigner(null)
              }}
              onFocus={() => {
                if (
                  designerSearchResults.length > 0 ||
                  designerSearchLoading ||
                  designerSearchError
                ) {
                  setDesignerDatabaseOpen(true)
                }
              }}
              className="w-full rounded border-2 border-blue-800 bg-white px-3 py-2 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
            />

            <button
              type="button"
              onClick={() => setDesignerDatabaseOpen(!designerDatabaseOpen)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-blue-800"
            >
              {designerDatabaseOpen ? (
              <FiChevronUp className="h-5 w-5 text-blue-800" />
            ) : (
              <FiChevronDown className="h-5 w-5 text-blue-800" />
            )}
            </button>
          </div>

          {designerDatabaseOpen && (
            <div className="absolute left-0 right-0 top-full z-20 max-h-96 overflow-y-auto rounded-b border-2 border-t-0 border-blue-800 bg-white text-sm shadow-sm">
              {designerSearchLoading && (
                <div className="px-4 py-3 text-gray-500">
                  <SpinnerText>Loading...</SpinnerText>
                </div>
              )}

              {designerSearchError && (
                <div className="px-4 py-3 text-red-600">
                  {designerSearchError}
                </div>
              )}

              {!designerSearchLoading &&
                !designerSearchError &&
                designerSearchResults.length === 0 && (
                <div className="px-4 py-3 text-gray-500">
                  No options available
                </div>
              )}

              {!designerSearchLoading &&
                !designerSearchError &&
                designerSearchResults.map((designer) => (
                  <button
                    key={designer.designerIdentifier}
                    type="button"
                    onClick={() => {
                      setSelectedDesigner(designer)
                      setDesignerSearch(designer.name)
                      setDesignerSearchResults([])
                      setDesignerDatabaseOpen(false)
                    }}
                    className="grid w-full grid-cols-[1fr_100px] border-b border-gray-200 px-4 py-2 text-left text-gray-800 last:border-b-0 hover:bg-blue-50"
                  >
                    <span>{designer.name}</span>
                    <span className="text-right">{designer.designerIdentifier}</span>
                  </button>
                ))}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleAddDatabaseDesigner}
          disabled={!selectedDesigner}
          className={`rounded border-2 px-6 py-2 text-sm font-semibold uppercase ${
            selectedDesigner
              ? "border-blue-800 bg-blue-800 text-white hover:bg-blue-700"
              : "border-gray-300 bg-white text-gray-300 cursor-not-allowed"
          }`}
        >
          Add designer
        </button>
      </div>
    </div>
  )}

  {designerDetailsOpen && designerMode === "new" && (
    <div className="relative mt-5 rounded bg-gray-50 p-4">
      <button
        type="button"
        onClick={() => {
          setDesignerMode(null)
          setDesignerName("")
        }}
        className="absolute right-3 top-3 inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-500 text-xs font-semibold text-white hover:bg-gray-600"
      >
        x
      </button>

      <label className="block text-sm text-gray-500">
        Enter name
      </label>

      <div className="mt-2 flex gap-4 pr-8">
        <input
          type="text"
          value={designerName}
          onChange={(event) => setDesignerName(event.target.value)}
          className="flex-1 rounded border-2 border-blue-800 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
        />

        <button
          type="button"
          onClick={handleAddDesigner}
          disabled={!designerName.trim()}
          className={`rounded border-2 px-6 py-2 text-sm font-semibold uppercase ${
            designerName.trim()
              ? "border-blue-800 bg-blue-800 text-white hover:bg-blue-700"
              : "border-gray-300 bg-white text-gray-300 cursor-not-allowed"
          }`}
        >
          Add designer
        </button>
      </div>
    </div>
  )}

  {editingDesignerIndex !== null && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="w-full max-w-md rounded-md bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h3 className="text-base font-semibold text-gray-800">
            Edit designer name
          </h3>
          <button
            type="button"
            onClick={() => setEditingDesignerIndex(null)}
            className="text-xl leading-none text-gray-700 hover:text-gray-900"
          >
            x
          </button>
        </div>

        <div className="p-4">
          <label className="block text-xs font-medium text-gray-600">
            Designer name
          </label>
          <input
            type="text"
            value={editingDesignerName}
            onChange={(event) =>
              setEditingDesignerName(event.target.value)
            }
            className="mt-2 w-full rounded border border-blue-700 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-700"
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-4 border-t border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={() => setEditingDesignerIndex(null)}
            className="px-4 py-2 text-sm text-blue-800 hover:text-blue-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={saveEditedDesigner}
            disabled={!editingDesignerName.trim()}
            className={`rounded px-4 py-2 text-sm text-white ${
              editingDesignerName.trim()
                ? "bg-blue-800 hover:bg-blue-700"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  )}
</div>
  )
}
